import React, { memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import RoutingGuideIcon from 'transfix-icons/build/RoutingGuideIcon'
import SearchIcon from 'transfix-icons/build/SearchIcon'
import ShipmentsIcon from 'transfix-icons/build/ShipmentsIcon'
import Button from 'transfix-ui/components/Button'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { TEST_IDS } from '../../../constants'

const useShipmentOffersEmptyStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    width: '100%',
    padding: theme.spacing(2),

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: theme.spacing(1.5),

    [theme.breakpoints.up('lg')]: {
      alignItems: 'center',
      width: 'auto',
      flexGrow: 1,
    },
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  skeletonTitle: {
    height: 20,
    width: 250,
  },
  skeletonSubText: {
    height: 17,
    width: 302,
  },
  iconContainer: {
    backgroundColor: brandColors.coolGray1,
    padding: `${theme.spacing(1.5)}`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '& svg': {
      width: theme.spacing(3),
      height: theme.spacing(3),

      [theme.breakpoints.up('md')]: {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
      },
    },
  },
  buttonContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),

    [theme.breakpoints.up('lg')]: {
      width: 'auto',
    },
  },
}))

export interface IShipmentOffersEmptyProps {
  /**
   * @default false
   */
  loading?: boolean
}

const ShipmentOffersEmpty = memo<IShipmentOffersEmptyProps>(({ loading = false }) => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const classes = useShipmentOffersEmptyStyles()

  const findLoadsClickHandler = useCallback(() => {
    history.push('/book')
    trackEvent('Homepage', 'HOME_OFFERS_CLICKED_FIND_LOADS_BUTTON')
  }, [history, trackEvent])

  const dedicatedLanesClickHandler = useCallback(() => {
    history.push('/dedicated-lanes/search')
    trackEvent('Homepage', 'HOME_OFFERS_CLICKED_DEDICATED_LANES_BUTTON')
  }, [history, trackEvent])

  return (
    <div className={classes.container} data-test={TEST_IDS.shipmentOffersEmpty}>
      <div className={classes.descriptionContainer}>
        <div className={classes.iconContainer}>
          <ShipmentsIcon size='large' color='coolGray5' />
        </div>
        {loading ? (
          <div className={classes.text}>
            <Skeleton variant='rectangular' className={classes.skeletonTitle} />
            <Skeleton variant='rectangular' className={classes.skeletonSubText} />
          </div>
        ) : (
          <div className={classes.text}>
            <Typography variant='subtitle1' component='div'>
              No load offers to accept right now.
            </Typography>
            <Typography variant='body2' component='div'>
              Bid on loads or dedicated feight to receive offers
            </Typography>
          </div>
        )}
      </div>
      {!loading && (
        <div className={classes.buttonContainer}>
          <Button
            color='secondary'
            size='large'
            startIcon={<SearchIcon size='medium' />}
            fullWidth
            data-test={TEST_IDS.shipmentOffersEmptyFindLoadsButton}
            onClick={findLoadsClickHandler}>
            Find&nbsp;Loads
          </Button>
          <Button
            color='secondary'
            size='large'
            startIcon={<RoutingGuideIcon size='medium' />}
            fullWidth
            data-test={TEST_IDS.shipmentOffersEmptyDedicatedLanesButton}
            onClick={dedicatedLanesClickHandler}>
            Dedicated&nbsp;Lanes
          </Button>
        </div>
      )}
    </div>
  )
})

export default ShipmentOffersEmpty
